import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../config/api';
import { useAuth } from '../context/AuthContext';

const TakerMakerFee = () => {
  const [takerMakerFees, setTakerMakerFees] = useState([]);
  const [filteredFees, setFilteredFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    exchange: '',
    symbol: '',
    type: '',
    status: ''
  });
  const [sortBy, setSortBy] = useState({
    column: 'symbol',
    direction: '1-9'
  });
  const [popup, setPopup] = useState({
    show: false,
    symbolId: null,
    symbolData: null,
    position: { x: 0, y: 0 }
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [expandedRow, setExpandedRow] = useState(null);
  const { user } = useAuth();
  const userId = user?.id || null; // Get userId from auth context, null if not logged in

  useEffect(() => {
    document.title = 'Taker Maker';
    fetchTakerMakerFees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, takerMakerFees, sortBy]);

  // Get unique exchange values for dropdown
  const getUniqueExchanges = () => {
    const exchanges = takerMakerFees
      .map(fee => fee.exchange_name)
      .filter(name => name && name.trim() !== '')
      .filter((name, index, arr) => arr.indexOf(name) === index)
      .sort();
    return ['', ...exchanges]; // Add empty option at beginning
  };

  // Get unique status values for dropdown
  const getUniqueStatuses = () => {
    const statuses = takerMakerFees
      .map(fee => fee.status)
      .filter(status => status && status.trim() !== '')
      .filter((status, index, arr) => arr.indexOf(status) === index)
      .sort();
    return ['', ...statuses]; // Add empty option at beginning
  };

  // Get unique category values for dropdown
  const getUniqueCategories = () => {
    const categories = takerMakerFees
      .map(fee => fee.category)
      .filter(category => category && category.trim() !== '')
      .filter((category, index, arr) => arr.indexOf(category) === index)
      .sort();
    return ['', ...categories]; // Add empty option at beginning
  };

  const applyFilters = () => {
    let filtered = takerMakerFees;

    if (filters.exchange) {
      filtered = filtered.filter(fee =>
        fee.exchange_name === filters.exchange
      );
    }

    if (filters.symbol) {
      filtered = filtered.filter(fee =>
        fee.symbol?.toLowerCase().includes(filters.symbol.toLowerCase())
      );
    }

    if (filters.type) {
      filtered = filtered.filter(fee =>
        fee.category === filters.type
      );
    }

    if (filters.status) {
      filtered = filtered.filter(fee =>
        fee.status === filters.status
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA, valueB;

      switch (sortBy.column) {
        case 'exchange_name':
          valueA = a.exchange_name || '';
          valueB = b.exchange_name || '';
          break;
        case 'symbol':
          valueA = a.symbol || '';
          valueB = b.symbol || '';
          break;
        case 'symbol_name':
          valueA = a.symbol_name || '';
          valueB = b.symbol_name || '';
          break;
        case 'taker_fee':
          valueA = parseFloat(a.taker_fee) || 0;
          valueB = parseFloat(b.taker_fee) || 0;
          break;
        case 'maker_fee':
          valueA = parseFloat(a.maker_fee) || 0;
          valueB = b.maker_fee || 0;
          break;
        case 'category':
          valueA = a.category || '';
          valueB = b.category || '';
          break;
        case 'status':
          valueA = a.status || '';
          valueB = b.status || '';
          break;
        case 'last_update_timestamp':
          valueA = new Date(a.last_update_timestamp || 0);
          valueB = new Date(b.last_update_timestamp || 0);
          break;
        default:
          valueA = a.symbol || '';
          valueB = b.symbol || '';
      }

      let comparison = 0;
      if (typeof valueA === 'string') {
        comparison = valueA.localeCompare(valueB);
      } else {
        comparison = valueA - valueB;
      }

      return sortBy.direction === '9-1' ? comparison : -comparison;
    });

    setFilteredFees(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      exchange: '',
      symbol: '',
      type: '',
      status: ''
    });
    setSortBy({
      column: 'symbol',
      direction: '1-9'
    });
  };

  const fetchSymbolDetails = async (symbolId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/symbols/${symbolId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          return data.data;
        }
      }
      return null;
    } catch (error) {
      console.error('Error fetching symbol details:', error);
      return null;
    }
  };

  const handleSymbolClick = async (event, symbolId) => {
    if (!symbolId) return;

    const rect = event.target.getBoundingClientRect();
    const position = {
      x: rect.right + 10,
      y: rect.top
    };

    setPopup({
      show: true,
      symbolId,
      symbolData: null,
      position
    });

    const symbolData = await fetchSymbolDetails(symbolId);
    setPopup(prev => ({
      ...prev,
      symbolData
    }));
  };

  const closePopup = () => {
    setPopup({
      show: false,
      symbolId: null,
      symbolData: null,
      position: { x: 0, y: 0 }
    });
  };

  const fetchTakerMakerFees = async () => {
    try {
      setLoading(true);
      setError(null);

      // Add userId as query parameter if logged in
      const url = userId && userId !== 0
        ? `${API_BASE_URL}/api/taker-maker-fees?userId=${userId}`
        : `${API_BASE_URL}/api/taker-maker-fees`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setTakerMakerFees(data.data);
        setFilteredFees(data.data);
      } else {
        throw new Error(data.error || 'Failed to fetch taker maker fees');
      }

      setLoading(false);

    } catch (err) {
      console.error('Error fetching taker maker fees:', err);
      setError('Failed to fetch taker maker fees data');
      setTakerMakerFees([]);
      setFilteredFees([]);
      setLoading(false);
    }
  };

  const formatLastUpdateDate = (dateString) => {
    if (!dateString) return { date: '-', time: '-' };

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return { date: '-', time: '-' };
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  };


  const formatFee = (fee) => {
    if (fee === null || fee === undefined) return '-';
    const value = (Number(fee) * 100).toFixed(4);
    return `${value}%`;
  };

  // Pagination calculations
  const totalPages = itemsPerPage === 'all' ? 1 : Math.ceil(filteredFees.length / itemsPerPage);
  const startIndex = itemsPerPage === 'all' ? 0 : (currentPage - 1) * itemsPerPage;
  const endIndex = itemsPerPage === 'all' ? filteredFees.length : startIndex + itemsPerPage;
  const currentPageData = filteredFees.slice(startIndex, endIndex);

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 'all' : parseInt(value));
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const toggleRow = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  // Render popup component
  const renderPopup = () => {
    if (!popup.show) return null;

    // Calculate position - adjust for small screens
    const isMobile = window.innerWidth <= 768;
    let positionStyle;

    if (isMobile) {
      // Center on mobile
      positionStyle = {
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        maxWidth: '90vw',
        maxHeight: '80vh',
        overflow: 'auto'
      };
    } else {
      // Position near click on desktop, but keep within viewport
      const maxRight = window.innerWidth - 320; // 300px width + 20px margin
      const maxBottom = window.innerHeight - 400; // approximate height

      positionStyle = {
        left: Math.min(popup.position.x, maxRight),
        top: Math.min(popup.position.y, Math.max(maxBottom, 20)),
        maxWidth: '300px'
      };
    }

    return (
      <>
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
            zIndex: 998
          }}
          onClick={closePopup}
        />
        <div
          style={{
            position: 'fixed',
            ...positionStyle,
            backgroundColor: '#1a1b23',
            border: '1px solid #2a2d3a',
            borderRadius: '4px',
            padding: '8px',
            zIndex: 999,
            minWidth: '200px',
            fontSize: '12px',
            fontFamily: "'Courier New', Courier, monospace",
            color: '#ffffff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
          }}
        >
          {/* Close X button */}
          <button
            onClick={closePopup}
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: 'transparent',
              border: 'none',
              color: '#8b949e',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '0',
              width: '20px',
              height: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1'
            }}
            onMouseEnter={(e) => e.target.style.color = '#ffffff'}
            onMouseLeave={(e) => e.target.style.color = '#8b949e'}
          >
            ×
          </button>

          {!popup.symbolData ? (
            <div>Loading symbol details...</div>
          ) : popup.symbolData ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px', fontSize: '12px', paddingRight: '20px' }}>
                {popup.symbolData.symbol?.toUpperCase()} Details
              </div>
              <div>Symbol Name: {popup.symbolData.symbol_name || '-'}</div>
              <div>Current Price: {popup.symbolData.current_price ? `$${Number(popup.symbolData.current_price).toFixed(2)}` : '-'}</div>
              <div>Market Cap: {popup.symbolData.market_cap ? Number(popup.symbolData.market_cap).toLocaleString() : '-'}</div>
              <div>Total Volume: {popup.symbolData.total_volume ? Number(popup.symbolData.total_volume).toLocaleString() : '-'}</div>
              <div>24h High: {popup.symbolData.high_24h ? `$${Number(popup.symbolData.high_24h).toFixed(2)}` : '-'}</div>
              <div>24h Low: {popup.symbolData.low_24h ? `$${Number(popup.symbolData.low_24h).toFixed(2)}` : '-'}</div>
              <div>24h Change: {popup.symbolData.price_change_24h ? `$${Number(popup.symbolData.price_change_24h).toFixed(2)}` : '-'}</div>
              <div>24h Change %: {popup.symbolData.price_change_percentage_24h ? `${Number(popup.symbolData.price_change_percentage_24h).toFixed(2)}%` : '-'}</div>
              <div>Circulating Supply: {popup.symbolData.circulating_supply ? Number(popup.symbolData.circulating_supply).toLocaleString() : '-'}</div>
              <div>ATH: {popup.symbolData.ath ? `$${Number(popup.symbolData.ath).toFixed(2)}` : '-'}</div>
              <div>ATL: {popup.symbolData.atl ? `$${Number(popup.symbolData.atl).toFixed(2)}` : '-'}</div>
              {popup.symbolData.image_url && (
                <div style={{ marginTop: '4px' }}>
                  <img src={popup.symbolData.image_url} alt={popup.symbolData.symbol} style={{ width: '16px', height: '16px' }} />
                </div>
              )}

              {/* Close button at bottom */}
              <button
                onClick={closePopup}
                style={{
                  marginTop: '8px',
                  padding: '4px 12px',
                  fontSize: '11px',
                  fontFamily: "'Courier New', Courier, monospace",
                  backgroundColor: '#238636',
                  color: '#ffffff',
                  border: '1px solid #238636',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  width: '100%'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2ea043';
                  e.target.style.borderColor = '#2ea043';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#238636';
                  e.target.style.borderColor = '#238636';
                }}
              >
                Close
              </button>
            </div>
          ) : (
            <div>Symbol details not available</div>
          )}
        </div>
      </>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        Loading taker maker fees data...
      </div>
    );
  }

  return (
    <div className="page-content">
      {error && (
        <div className="error" style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8d7da', color: '#721c24', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'inline-block' }}>
      <div className="filters" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#1a1b23', border: '1px solid #2a2d3a', borderRadius: '4px', fontFamily: "'Courier New', Courier, monospace" }}>
        <div style={{ display: 'flex', gap: '20px' }}>
          {/* Left column - Filter components */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '80px', marginRight: '10px' }}>Exchange:</label>
            <select
              value={filters.exchange}
              onChange={(e) => handleFilterChange('exchange', e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            >
              {getUniqueExchanges().map((exchange, index) => (
                <option key={index} value={exchange}>
                  {exchange || 'All Exchanges'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '80px', marginRight: '10px' }}>Symbol:</label>
            <input
              type="text"
              value={filters.symbol}
              onChange={(e) => handleFilterChange('symbol', e.target.value)}
              placeholder="Filter by symbol"
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '80px', marginRight: '10px' }}>Type:</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            >
              {getUniqueCategories().map((category, index) => (
                <option key={index} value={category}>
                  {category || 'All Types'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '80px', marginRight: '10px' }}>Status:</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '200px'
              }}
            >
              {getUniqueStatuses().map((status, index) => (
                <option key={index} value={status}>
                  {status || 'All Statuses'}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ color: '#8b949e', fontSize: '12px', minWidth: '80px', marginRight: '10px' }}>Sort:</label>
            <select
              value={sortBy.column}
              onChange={(e) => setSortBy(prev => ({ ...prev, column: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '120px',
                marginRight: '5px'
              }}
            >
              <option value="exchange_name">Exchange</option>
              <option value="symbol">Symbol</option>
              <option value="symbol_name">Symbol Name</option>
              <option value="taker_fee">Taker Fee</option>
              <option value="maker_fee">Maker Fee</option>
              <option value="category">Type</option>
              <option value="status">Status</option>
              <option value="last_update_timestamp">Last Update</option>
            </select>
            <select
              value={sortBy.direction}
              onChange={(e) => setSortBy(prev => ({ ...prev, direction: e.target.value }))}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#21262d',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: '#ffffff',
                width: '70px'
              }}
            >
              <option value="1-9">1→9</option>
              <option value="9-1">9→1</option>
            </select>
          </div>
          <div style={{ marginTop: '5px', display: 'flex', gap: '10px' }}>
            <button
              onClick={clearFilters}
              style={{
                padding: '4px 12px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#f85149',
                border: 'none',
                borderRadius: '3px',
                color: '#ffffff',
                cursor: 'pointer'
              }}
            >
              Clear
            </button>
          </div>
          </div>

          {/* Right column - Refresh button */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }} className="hide-mobile">
            <button
              onClick={() => {
                fetchTakerMakerFees();
              }}
              disabled={loading}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: '#238636',
                color: '#ffffff',
                border: '1px solid #238636',
                borderRadius: '3px',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1,
                transition: 'all 0.2s',
                width: '80px'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#2ea043';
                  e.target.style.borderColor = '#2ea043';
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = '#238636';
                  e.target.style.borderColor = '#238636';
                }
              }}
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Records per page selector */}
      <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <label style={{ color: '#8b949e', fontSize: '12px', whiteSpace: 'nowrap', fontFamily: "'Courier New', Courier, monospace" }}>Records per page:</label>
        <select
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(e.target.value)}
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            fontFamily: "'Courier New', Courier, monospace",
            backgroundColor: '#21262d',
            border: '1px solid #2a2d3a',
            borderRadius: '3px',
            color: '#ffffff',
            width: '80px'
          }}
        >
          <option value="50">50</option>
          <option value="100">100</option>
          <option value="all">All</option>
        </select>
      </div>

      <div className="data-table">
        <table>
          <thead>
            <tr>
              <th>Exchange<br/>Name</th>
              <th>Symbol</th>
              <th className="hide-mobile">Symbol<br/>Name</th>
              <th style={{ textAlign: 'right' }}>Taker<br/>Fee</th>
              <th style={{ textAlign: 'right' }}>Maker<br/>Fee</th>
              <th className="hide-mobile">Type</th>
              <th className="hide-mobile">Status</th>
              <th>Last<br/>Update</th>
            </tr>
          </thead>
          <tbody>
            {currentPageData.map((fee) => (
              <React.Fragment key={fee.id}>
                <tr
                  onClick={() => toggleRow(fee.id)}
                  style={{ cursor: 'pointer' }}
                  className="takermaker-row"
                >
                  <td>{fee.exchange_name || '-'}</td>
                  <td
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSymbolClick(e, fee.symbol_id);
                    }}
                  >
                    {fee.symbol?.toUpperCase() || '-'}
                  </td>
                  <td title={fee.symbol_name} className="hide-mobile">
                    {fee.symbol_name || '-'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{formatFee(fee.taker_fee)}</td>
                  <td style={{ textAlign: 'right' }}>{formatFee(fee.maker_fee)}</td>
                  <td className="hide-mobile">{fee.category || '-'}</td>
                  <td className="hide-mobile">
                    <span className={`status-badge ${fee.status?.toLowerCase()}`}>
                      {fee.status || '-'}
                    </span>
                  </td>
                  <td style={{ padding: '2px 4px', lineHeight: '1.2' }}>
                    <div style={{ color: '#8b949e' }}>
                      {formatLastUpdateDate(fee.last_update_timestamp).date}
                    </div>
                    <div style={{ color: '#8b949e' }}>
                      {formatLastUpdateDate(fee.last_update_timestamp).time}
                    </div>
                  </td>
                </tr>
                {expandedRow === fee.id && (
                  <tr className="mobile-details">
                    <td colSpan="8" style={{ padding: '8px', backgroundColor: '#161b22' }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px' }}>
                        <div><strong>Symbol Name:</strong> {fee.symbol_name || '-'}</div>
                        <div><strong>Type:</strong> {fee.category || '-'}</div>
                        <div><strong>Status:</strong> <span className={`status-badge ${fee.status?.toLowerCase()}`}>{fee.status || '-'}</span></div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ color: '#8b949e', fontSize: '10px', fontFamily: "'Courier New', Courier, monospace", textAlign: 'center' }}>
          Showing: {startIndex + 1}-{Math.min(endIndex, filteredFees.length)} of {filteredFees.length} filtered records ({takerMakerFees.length} total)
        </div>
        {itemsPerPage !== 'all' && totalPages > 1 && (
          <div style={{ color: '#8b949e', fontSize: '10px', fontFamily: "'Courier New', Courier, monospace", textAlign: 'center' }}>
            Page {currentPage} of {totalPages}
          </div>
        )}

        {itemsPerPage !== 'all' && totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'center' }}>
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '2px 6px',
                fontSize: '11px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: currentPage === 1 ? '#21262d' : '#3fb950',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: currentPage === 1 ? '#8b949e' : '#ffffff',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                minWidth: '30px'
              }}
            >
              &lt;
            </button>

            <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (currentPage <= 4) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = currentPage - 3 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => goToPage(pageNum)}
                    style={{
                      padding: '2px 6px',
                      fontSize: '11px',
                      fontFamily: "'Courier New', Courier, monospace",
                      backgroundColor: currentPage === pageNum ? '#3fb950' : '#21262d',
                      border: '1px solid #2a2d3a',
                      borderRadius: '3px',
                      color: '#ffffff',
                      cursor: 'pointer',
                      fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                      minWidth: '26px'
                    }}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '2px 6px',
                fontSize: '11px',
                fontFamily: "'Courier New', Courier, monospace",
                backgroundColor: currentPage === totalPages ? '#21262d' : '#3fb950',
                border: '1px solid #2a2d3a',
                borderRadius: '3px',
                color: currentPage === totalPages ? '#8b949e' : '#ffffff',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                minWidth: '30px'
              }}
            >
              &gt;
            </button>
          </div>
        )}
      </div>
      </div>

      {renderPopup()}
    </div>
  );
};

export default TakerMakerFee;